// Top-level orchestrator for the persistent right-rail panel.
//
// Owns the tab array passed to RightPanelShell: Overview (always first),
// Diplomacy (always second), and a dismissible Selection tab that appears
// only when `selected !== null`. Selection tab content delegates to
// SelectionTab, which routes to tile / city / unit content based on the
// selected SceneNode's type.
//
// Strictly declarative — no useState here. Tab state lives in RightPanelShell.

import type { SceneNode } from '@aod/protocol';
import type { CitySuggestion } from '@aod/sim-adapter';
import type { PlayerHudEntry } from '@aod/ui';
import type { Diplomacy } from '../../dynasty/diplomacy.js';
import type { Dynasty } from '../../dynasty/dynasty.js';
import { pendingOffers } from '../../dynasty/pendingOffers.js';
import type { Notice } from '../../dynasty/useTreatyNews.js';
import type { Espionage } from '../../espionage/espionage.js';
import type { HeroCaptivity } from '../../heroes/captivity.js';
import type { HeroQuestKind, HeroQuests } from '../../heroes/quests.js';
import { type MessageKey, type Translate, useT } from '../../i18n/index.js';
import { useIsPhone } from '../../lib/useIsPhone.js';
import { findPendingMerchantOffer } from '../../sea/pendingOffer.js';
import type { CaptureChoice } from '../../sim/useSimWorld/applyIntent.js';
import type { OpsActions } from '../../sim/useSimWorld/useEspionageIntents.js';
import type { TreatyKind } from '../../sim/useSimWorld/useTreatyIntents.js';
import type { Archetype } from '../DoctrinePanel/archetypes.js';
import type { BuildingDef } from '../InfoPanel/BuildingSection.js';
import { DiplomacyTab } from './DiplomacyTab.js';
import { MissionsTab } from './MissionsTab.js';
import { OverviewTab } from './OverviewTab.js';
import { RightPanelShell, type TabDef } from './RightPanelShell.js';
import { SelectionTab } from './SelectionTab.js';
import type { FormattableEvent } from './formatEventPayload.js';
import type { DoctrineState, VictoryProgress } from './types.js';

export interface RightPanelProps {
  readonly selected: SceneNode | null;
  readonly snapshot: { scene: { children?: SceneNode[] } };
  readonly localPlayerIndex: number;
  readonly matchOver: boolean;
  readonly garrisonCount: number;
  readonly buildings: ReadonlyArray<BuildingDef>;
  readonly treasuryIndustry: number;
  readonly tick: bigint;
  readonly events: readonly FormattableEvent[];
  readonly victoryProgress: VictoryProgress | null;
  readonly localPlayer: DoctrineState | null;
  readonly playerHudEntries: readonly PlayerHudEntry[];
  readonly playerColors: ReadonlyMap<number, string>;
  readonly onClose: () => void;
  readonly onAttack: (cityId: string) => void;
  readonly onDeploy: (cityId: string) => void;
  readonly onStartBuilding: (cityId: string, buildingId: string) => void;
  /** Take the offer a foreign house has made to a harbour. */
  readonly onAcceptMerchant: (cityId: string) => void;
  /** Refuse it. */
  readonly onDeclineMerchant: (cityId: string) => void;
  readonly onSetDoctrine: (cityId: string, archetype: Archetype) => void;
  /** The royal families, while `diplomacy.dynasty` is on. */
  readonly dynasty?: Dynasty | null;
  /** The spies of the local house, while `espionage.enabled` is on. */
  readonly espionage?: Espionage | null;
  /** Send a spy to watch an enemy city. */
  readonly onDispatchSpy?: (unitId: string, targetCityId: string) => boolean;
  /** The hostile orders (`espionage.ops`). */
  readonly opsActions?: OpsActions;
  /** Decide what happens to a royal you hold. */
  readonly onChooseCapture?: (royalId: number, choice: CaptureChoice) => boolean;
  /** Walk a royal out of where they are kept. */
  readonly onSpyRescue?: (unitId: string, royalId: number) => boolean;
  /** Take the place a royal is kept by force. */
  readonly onRaidRescue?: (royalId: number) => boolean;
  /** Buy a royal back. */
  readonly onPayRansom?: (royalId: number) => boolean;
  /** The heroes in captivity, while `heroes.captivity` is on. */
  readonly heroCaptivity?: HeroCaptivity | null;
  /** Decide what happens to a hero you hold. */
  readonly onChooseHeroCaptivity?: (unitId: string, choice: CaptureChoice) => boolean;
  /** Buy a hero of your house back. */
  readonly onPayHeroRansom?: (unitId: string) => boolean;
  /** The orders the local house's heroes carry, while `heroes.quests` is on. */
  readonly heroQuests?: HeroQuests | null;
  /** Give a hero an order. */
  readonly onDispatchHeroQuest?: (
    unitId: string,
    kind: HeroQuestKind,
    amount?: number,
    targetUnitId?: string,
  ) => boolean;
  /** Offer a marriage to an allied house. */
  readonly onProposeMarriage?: (toPlayer: number) => void;
  /** Who stands where, and the offers on the table, while `diplomacy.basic` is on. */
  readonly diplomacy?: Diplomacy | null;
  readonly onProposeTreaty?: (toPlayer: number, treaty: TreatyKind) => void;
  readonly onAnswerTreaty?: (fromPlayer: number, accept: boolean) => void;
  readonly onDeclareWar?: (toPlayer: number) => void;
  /** Answer a marriage offered to you (the toast having faded, the tab keeps it). */
  readonly onAnswerMarriage?: (fromPlayer: number, accept: boolean) => void;
  /** The last few treaty events, newest first. */
  readonly treatyHistory?: readonly Notice[];
  readonly suggestions: readonly CitySuggestion[];
  readonly onApplySuggestion: (idea: CitySuggestion) => void;
  readonly onActivateAbility: (unitId: string, slotIndex: number) => void;
  readonly onSelectAbility: (unitId: string, slotIndex: number, abilityId: string) => void;
  readonly onSetFormation: (unitId: string, formation: string) => void;
  /** Phone layout: the info rail is open as a bottom sheet. */
  readonly mobileOpen?: boolean;
}

// SceneNode['type'] is `string` at the protocol layer, so this Record is a
// lookup-only convenience — it does NOT enforce exhaustive coverage (a new
// node type would silently fall through to `null` rather than error at compile
// time). If protocol ever narrows `type` to a literal union, swap the
// `Record<string, ...>` for `Record<SceneNode['type'], ...>` to get the
// exhaustiveness guarantee.
const SELECTION_LABELS: Record<string, MessageKey> = {
  tile: 'selection.tile',
  city: 'selection.city',
  unit: 'selection.unit',
};

function selectionLabel(t: Translate, selected: SceneNode | null): string | null {
  if (selected === null) return null;
  const key = SELECTION_LABELS[selected.type];
  return key ? t(key) : null;
}

export function RightPanel(props: RightPanelProps) {
  const { selected, onClose } = props;
  const t = useT();
  const phone = useIsPhone();
  const label = selectionLabel(t, selected);
  const hasSelection = selected !== null && label !== null;

  // The same list the toasts draw from: what is still waiting on you.
  const merchantOffer = props.matchOver
    ? null
    : findPendingMerchantOffer(props.snapshot, props.localPlayerIndex);
  const waiting = props.matchOver
    ? 0
    : pendingOffers(
        props.diplomacy ?? null,
        props.dynasty ?? null,
        merchantOffer,
        props.localPlayerIndex,
      ).length;

  const persistentTabs: TabDef[] = [
    {
      id: 'overview',
      label: t('rightPanel.overview'),
      content: (
        <OverviewTab
          tick={props.tick}
          events={props.events}
          victoryProgress={props.victoryProgress}
          localPlayer={props.localPlayer}
        />
      ),
    },
    {
      id: 'diplomacy',
      label: t('rightPanel.diplomacy'),
      badge: waiting,
      badgeLabel: t('treaty.offers.badge', { count: waiting }) as string,
      content: (
        <DiplomacyTab
          players={props.playerHudEntries}
          playerColors={props.playerColors}
          dynasty={props.dynasty}
          localPlayerIndex={props.localPlayerIndex}
          onProposeMarriage={props.onProposeMarriage}
          diplomacy={props.diplomacy}
          onProposeTreaty={props.onProposeTreaty}
          onAnswerTreaty={props.onAnswerTreaty}
          onDeclareWar={props.onDeclareWar}
          onAnswerMarriage={props.onAnswerMarriage}
          merchantOffer={merchantOffer}
          onAnswerMerchant={(cityId, accept) =>
            accept ? props.onAcceptMerchant(cityId) : props.onDeclineMerchant(cityId)
          }
          history={props.treatyHistory}
          matchOver={props.matchOver}
        />
      ),
    },
  ];

  // The mission tab appears once the modules that fill it are on: a house
  // with spies to send, with a royal in chains, or with a hero held.
  const missionsTab: TabDef | null =
    props.espionage || props.dynasty || props.heroCaptivity || props.heroQuests
      ? {
          id: 'missions',
          label: t('espionage.tab'),
          content: (
            <MissionsTab
              espionage={props.espionage}
              dynasty={props.dynasty}
              heroCaptivity={props.heroCaptivity}
              heroQuests={props.heroQuests}
              localPlayerIndex={props.localPlayerIndex}
              players={props.playerHudEntries}
              onDispatchSpy={props.onDispatchSpy}
              opsActions={props.opsActions}
              onChooseCapture={props.onChooseCapture}
              onSpyRescue={props.onSpyRescue}
              onRaidRescue={props.onRaidRescue}
              onPayRansom={props.onPayRansom}
              onChooseHeroCaptivity={props.onChooseHeroCaptivity}
              onPayHeroRansom={props.onPayHeroRansom}
            />
          ),
        }
      : null;

  // TypeScript can't narrow `selected` from `hasSelection` (a boolean derived
  // from `selected !== null && label !== null`), so re-narrow explicitly. The
  // `label !== null` branch is unreachable here but is required for the
  // `selectionTab.label` slot, which expects `string`.
  const selectionTab: TabDef | null =
    hasSelection && selected !== null && label !== null
      ? {
          id: 'selection',
          label,
          content: (
            <SelectionTab
              selected={selected}
              snapshot={props.snapshot}
              localPlayerIndex={props.localPlayerIndex}
              matchOver={props.matchOver}
              garrisonCount={props.garrisonCount}
              buildings={props.buildings}
              treasuryIndustry={props.treasuryIndustry}
              onAttack={props.onAttack}
              onDeploy={props.onDeploy}
              onStartBuilding={props.onStartBuilding}
              onAcceptMerchant={props.onAcceptMerchant}
              onDeclineMerchant={props.onDeclineMerchant}
              onSetDoctrine={props.onSetDoctrine}
              suggestions={props.suggestions}
              onApplySuggestion={props.onApplySuggestion}
              onActivateAbility={props.onActivateAbility}
              onSelectAbility={props.onSelectAbility}
              onSetFormation={props.onSetFormation}
              heroQuests={props.heroQuests}
              onDispatchHeroQuest={props.onDispatchHeroQuest}
              followSelection={phone}
            />
          ),
          dismissible: true,
          onDismiss: onClose,
        }
      : null;

  const tabs: readonly TabDef[] = [
    ...persistentTabs,
    ...(missionsTab === null ? [] : [missionsTab]),
    ...(selectionTab === null ? [] : [selectionTab]),
  ];

  return (
    <RightPanelShell
      title={hasSelection ? label : t('rightPanel.matchOverview')}
      tabs={tabs}
      defaultTabId={hasSelection ? 'selection' : 'overview'}
      mobileOpen={props.mobileOpen}
      followSelectionKey={phone ? (selected?.id ?? null) : undefined}
    />
  );
}
